import type { Location } from 'react-router-dom'

import type { ClientLibraryId } from 'types/client-library'

/**
 * URL for the backend auth-transfer redirect that opens a catalog's own site
 * with the user already signed in. This is a backend route, so it must be a
 * full page load (plain anchor), not a react-router navigation.
 */
export const getAuthTransferUrl = (clientLibraryId: ClientLibraryId): string =>
  `/api-auth/transfer-start/?client_library_id=${clientLibraryId}`

const LICENSE_REQUESTS_BASE_PATTERN = /^\/catalogs\/[^/]+\/license_requests/

/**
 * Returns the current section's base route, so shortlist/playlist/track
 * modals (save, share, copy metadata, preview metadata, etc.) nest their
 * sub-routes under whichever page opened them instead of a fixed location.
 * @param location - The current location object
 * @returns '/search' if in search context, the matched
 * `/catalogs/:libraryId/license_requests` prefix if in a license-requests
 * context, empty string otherwise
 */
export const getShortlistBaseRoute = (location?: Location): string => {
  if (location?.pathname?.startsWith('/search')) return '/search'
  return location?.pathname?.match(LICENSE_REQUESTS_BASE_PATTERN)?.[0] ?? ''
}

/**
 * Returns the route for the third-party rights data lookup modal, nesting it
 * under whichever page opened the track metadata modal (search,
 * license-requests) so closing it returns to the same page, instead of
 * always sending the user to a fixed location.
 */
export const getRightsDataLookupRoute = (
  pathname: string,
  trackId: number | string,
): string => {
  if (pathname.startsWith('/search')) {
    return `/search/track/${trackId}/rights-data`
  }
  const licenseRequestsBase = pathname.match(LICENSE_REQUESTS_BASE_PATTERN)?.[0]
  return licenseRequestsBase
    ? `${licenseRequestsBase}/tracks/${trackId}/rights-data`
    : `/explorer/tracks/${trackId}/rights-data`
}

/**
 * Returns the root return path for a given pathname.
 * Used by selection modals to redirect back to the originating page.
 */
export const getSelectionReturnPath = (pathname: string): string => {
  return pathname.startsWith('/search/') ? '/search' : '/'
}

/**
 * Returns the selection route base for a given pathname.
 * e.g. '/search/selection' when in search context, '/selection' otherwise.
 */
export const getSelectionBase = (pathname: string): string => {
  return pathname.startsWith('/search/') ? '/search/selection' : '/selection'
}
